import React, { createContext, useState, useEffect, useRef, Dispatch, SetStateAction } from "react";
import { useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { isEmbedDashboard } from "../../app/components/dashboard/embedDashboard";
import { StriimAISidebarPanel } from "./striim-ai-sidebar/striim-ai-sidebar";
import { useAppSelector, useAppDispatch } from "src/stores/redux/hooks";
import {
    selectIsSidebarMaximized,
    selectIsSidebarOpen,
    setSidebarMaximizedAction
} from "src/stores/redux/features/striim-ai/striim-ai.slice";

// ── Layout constants ───────────────────────────────────────────────────────────

const EXPANDED_NAVBAR_WIDTH = 174;
const COLLAPSED_NAVBAR_WIDTH = 72;
const STRIIM_AI_SIDEBAR_WIDTH = 450;
const CONVO_SIDEBAR_VW = 0.4;
const CONVO_SIDEBAR_CSS = `${CONVO_SIDEBAR_VW * 100}vw`;
export const FD_LEFT_SIDEBAR_WIDTH = 325; // Change also @left-sidebar-width

// ── Navbar context ─────────────────────────────────────────────────────────────

type NavbarContextType = {
    isNavbarToggled: boolean;
    isConversationalMode: boolean;
    setIsConversationalMode: Dispatch<SetStateAction<boolean>>;
};

export const NavbarContext = createContext<NavbarContextType | null>(null);

// ── Sidebar slot ───────────────────────────────────────────────────────────────
// Renders as two siblings:
//   1. A flex spacer that pushes #page-content inward as the sidebar opens.
//   2. A position:fixed overlay panel so Backbone's #content is unaffected.

interface SidebarSlotProps {
    isOpen: boolean;
    isConversational: boolean;
    hasEverOpened: boolean;
    isNavbarToggled: boolean;
}

const SidebarSlot: React.FC<SidebarSlotProps> = ({ isOpen, isConversational, hasEverOpened, isNavbarToggled }) => {
    const isMaximized = useAppSelector(selectIsSidebarMaximized);
    const collapsedWidth = isConversational ? CONVO_SIDEBAR_CSS : STRIIM_AI_SIDEBAR_WIDTH;
    const navbarWidth = isNavbarToggled ? EXPANDED_NAVBAR_WIDTH : COLLAPSED_NAVBAR_WIDTH;

    return (
        <>
            {/* Flex spacer — reserves layout space so #page-content shrinks.
                When maximized the panel overlays everything, so no space is reserved. */}
            <div
                style={{
                    width: isOpen && !isMaximized ? collapsedWidth : 0,
                    flexShrink: 0,
                    transition: "width 0.3s ease"
                }}
            />

            {/* Fixed visual panel */}
            <Box
                sx={{
                    position: "fixed",
                    right: 0,
                    top: "49px",
                    width: isOpen ? (isMaximized ? `calc(100vw - ${navbarWidth}px)` : collapsedWidth) : 0,
                    height: "calc(100vh - 49px)",
                    overflow: "hidden",
                    transition: "width 0.3s ease",
                    borderLeft: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "greyscale.50",
                    display: "flex",
                    flexDirection: "column",
                    zIndex: 6
                }}
            >
                <Box
                    sx={{
                        opacity: isOpen ? 1 : 0,
                        transition: "opacity 0.2s ease",
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0
                    }}
                >
                    {/* Lazy-mount: keep panel alive once opened to preserve chat history */}
                    {hasEverOpened && <StriimAISidebarPanel />}
                </Box>
            </Box>
        </>
    );
};

// ── PageFrame ──────────────────────────────────────────────────────────────────

const PageFrame = ({ Navbar, Topbar, children }) => {
    const chatbotOpen = useAppSelector(selectIsSidebarOpen);
    const dispatch = useAppDispatch();
    const location = useLocation();
    const [isNavbarToggled, setIsNavbarToggled] = useState<boolean>(false);
    const [isConversationalMode, setIsConversationalMode] = useState<boolean>(false);
    const hasOpenedRef = useRef(false);

    if (chatbotOpen) hasOpenedRef.current = true;

    // ── Helpers ────────────────────────────────────────────────────────────────

    // Adjusts both the Backbone (#content) and React (#page-content) widths
    // whenever the navbar collapses or expands.
    function setContentLayout(currentWidth: number) {
        setIsNavbarToggled(currentWidth === EXPANDED_NAVBAR_WIDTH);

        const backboneContent = document.getElementById("content");
        const reactContent = document.getElementById("page-content");

        if (isEmbedDashboard()) {
            backboneContent!.style.width = "100%";
            backboneContent!.style.marginLeft = "0";
            reactContent!.style.display = "none";
            return;
        }

        backboneContent!.style.width = `calc(100vw - ${currentWidth + 8}px)`;
        backboneContent!.style.marginLeft = `${currentWidth}px`;
        // Navbar is position:fixed so it doesn't occupy flex space — set marginLeft manually.
        reactContent!.style.marginLeft = `${currentWidth}px`;

        const fdContent = document.querySelector<HTMLElement>(".with-left-menu");
        const fdRightContent = document.querySelector<HTMLElement>(".flow-designer-root .right-content");
        const fdRightSidebarToggled = document.querySelector<HTMLElement>(".flow-designer-root .right-sidebar");
        if (fdRightSidebarToggled) {
            fdRightSidebarToggled.classList.toggle("navbar-collapsed", isNavbarToggled);
        }
        if (fdContent) {
            fdContent.style.left = `${currentWidth + FD_LEFT_SIDEBAR_WIDTH}px`;
        } else if (fdRightContent) {
            fdRightContent.style.left = `${currentWidth}px`;
        }
    }

    // ── Effects ────────────────────────────────────────────────────────────────

    useEffect(() => {
        setContentLayout(EXPANDED_NAVBAR_WIDTH);
    }, []);

    // Collapse Striim AI full-window mode on navigation (only while the sidebar is open)
    useEffect(() => {
        if (chatbotOpen) dispatch(setSidebarMaximizedAction(false));
    }, [location.pathname, chatbotOpen, dispatch]);

    // Adjust Backbone (#content) width when sidebar or navbar state changes.
    // React #page-content is handled automatically by flexbox.
    useEffect(() => {
        if (isEmbedDashboard()) return;
        const navbarWidth = isNavbarToggled ? EXPANDED_NAVBAR_WIDTH : COLLAPSED_NAVBAR_WIDTH;
        const sidebarWidthPx = isConversationalMode
            ? Math.round(window.innerWidth * CONVO_SIDEBAR_VW)
            : STRIIM_AI_SIDEBAR_WIDTH;
        const sidebarOffset = chatbotOpen ? sidebarWidthPx : 0;
        const backboneContent = document.getElementById("content");
        if (backboneContent) {
            backboneContent.style.width = `calc(100vw - ${navbarWidth + 8 + sidebarOffset}px)`;
        }
        document.body.classList.toggle("striim-ai-sidebar-open", chatbotOpen);
    }, [chatbotOpen, isNavbarToggled, isConversationalMode]);

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div style={{ display: "flex" }}>
            <NavbarContext.Provider
                value={{
                    isNavbarToggled,
                    isConversationalMode,
                    setIsConversationalMode
                }}
            >
                <Navbar setContentLayout={setContentLayout} />

                {/* flex:1 + minWidth:0 lets this shrink when the sidebar opens */}
                <div id="page-content" style={{ flex: 1, minWidth: 0 }}>
                    <>{Topbar}</>
                    <>{children}</>
                </div>

                <SidebarSlot
                    isOpen={chatbotOpen}
                    isConversational={isConversationalMode}
                    hasEverOpened={hasOpenedRef.current}
                    isNavbarToggled={isNavbarToggled}
                />
            </NavbarContext.Provider>
        </div>
    );
};

export default PageFrame;
